import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { buildApiUrl } from "../services/apiHelpers";
import web_logoImage from "../assets/images/web_logo.png";
import ManageSessions from "./ManageSessions";
import { useSubscription } from "../context/SubscriptionContext";

import {
  Menu,
  X,
  Home,
  Hospital,
  Users,
  Calendar,
  FileText,
  TrendingUp,
  Settings,
  CreditCard,
  CheckCircle
} from "lucide-react";

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@600&display=swap');

/* ── Reset & Variable Tokens ── */
*, *::before, *::after { 
  box-sizing: border-box; 
  margin: 0; 
  padding: 0; 
}

:root {
  --sb-bg: #030712; /* Near-black blue foundation */
  --sb-bg-strong: #02050a; /* Darkest anchor color */
  --sb-surface-hover: rgba(255, 255, 255, 0.12);
  --sb-brand: #e2e8f0;
  --sb-brand-light: rgba(226, 232, 240, 0.16);
  --sb-brand-border: rgba(226, 232, 240, 0.2);
  --sb-text-main: #f8fafc;
  --sb-text-muted: #94a3b8; /* Soft slate text */
  --sb-border: rgba(255, 255, 255, 0.12);
  --sb-font: 'Plus Jakarta Sans', sans-serif;
}

/* ── Premium Solid Shell ── */
.sb {
  width: 280px;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 300;
  display: flex;
  flex-direction: column;
  font-family: var(--sb-font);
  background: linear-gradient(180deg, #0b0f27 0%, #0b215d 100%);
  border-right: 1px solid var(--sb-border);
  box-shadow: inset 0 0 40px rgba(2, 6, 23, 0.45);
  overflow: hidden;
  -webkit-font-smoothing: antialiased;
  justify-content: flex-start;
  align-items: stretch;
}


/* ── Upper Branding Deck ── */
.sb-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  height: 88px;
  flex-shrink: 0;
  border-bottom: none;
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  gap: 12px;
}

.sb-web_logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  gap: 8px;
}

.sb-web_logo {
  height: 96px;
  width: auto;
  object-fit: contain;
  display: block;
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.sb-brand-divider {
  width: 1px;
  min-height: 34px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  margin: 0 14px;
  align-self: center;
}

@media (max-width: 767px) {
  .sb-web_logo {
    display: block;
    height: 80px;
  }
}

.sb-header::after {
  content: '';
  position: absolute;
  bottom: 10px;
  left: 14px;
  right: 14px;
  height: 2px;
  background: rgba(255,255,255,0.06);
  box-shadow: 0 2px 6px rgba(2,12,34,0.14) inset;
  border-radius: 2px;
}

.sb-clinic-block {
  display: none;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.sb-clinic-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.sb-clinic-icon {
  color: #ffd166;
  flex-shrink: 0;
}

.sb-clinic-name {
  font-size: 14px;
  font-weight: 700;
  color: #ffd166;
  letter-spacing: -0.3px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sb-clinic-code {
  font-size: 9px;
  color: #ffb703;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  font-family: 'JetBrains Mono', monospace;
}

/* Close action button */
.sb-close {
  width: 38px;
  height: 38px;
  border: 1px solid rgba(0, 217, 255, 0.15);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(0, 217, 255, 0.08) 0%, rgba(6, 182, 212, 0.05) 100%);
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #ffffff;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.sb-close:hover {
  background: linear-gradient(135deg, rgba(0, 217, 255, 0.15) 0%, rgba(6, 182, 212, 0.12) 100%);
  color: #00d9ff;
  border-color: rgba(0, 217, 255, 0.35);
  box-shadow: 0 4px 12px rgba(0, 217, 255, 0.15);
  transform: scale(1.05);
}

@media (max-width: 767px) {
  .sb-close {
    display: flex;
  }
}

/* ── Scrollable Track Body ── */
.sb-body {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 0 16px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.28) transparent;
}

.sb-body::-webkit-scrollbar {
  width: 6px;
}

.sb-body::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.28);
  border-radius: 999px;
}

.sb-section {
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px; /* provide space for divider */
  position: relative;
}

.sb-body > .sb-section:first-of-type {
  padding-top: 6px;
}

.sb-section + .sb-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 18px;
  right: 18px;
  height: 2px;
  background: rgba(255,255,255,0.06);
  box-shadow: 0 1px 0 rgba(255,255,255,0.03) inset, 0 1px 6px rgba(8,18,40,0.12);
  pointer-events: none;
  transform: translateY(-8px);
  border-radius: 2px;
  opacity: 1;
}

.sb-section + .sb-section::after {
  content: '';
  position: absolute;
  top: 0;
  left: 14px;
  width: 6px;
  height: 6px;
  background: rgba(220,236,255,0.30);
  border-radius: 2px;
  transform: translateY(-10px);
  pointer-events: none;
}

.sb-section-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--sb-text-muted);
  opacity: 0.7;
  padding: 0 24px 4px;
  user-select: none;
}

.sb-nav-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 12px;
}

/* ── Core Navigation Architecture ── */
.sb-nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 5px 14px;
  min-height: 32px;
  width: 100%;
  border-radius: 8px;
  text-decoration: none;
  color: var(--sb-text-muted);
  font-size: 13.5px;
  font-weight: 500;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
}

.sb-nav-item:hover:not(.active) {
  background: rgba(255, 255, 255, 0.16);
  color: var(--sb-text-main);
  transform: translateX(4px);
}

.sb-nav-item.disabled,
.sb-nav-item.disabled:hover {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
  background: transparent;
  color: var(--sb-text-muted);
}

.sb-nav-item.active {
  background: rgba(251, 146, 60, 0.22); /* Orange selected highlight */
  color: #ffffff;
  font-weight: 700;
  border-color: rgba(251, 146, 60, 0.35);
}

.sb-nav-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: inherit;
  transition: transform 0.2s ease;
}

.sb-nav-item:hover .sb-nav-icon {
  transform: scale(1.05);
}

.sb-nav-label {
  flex: 1;
}

/* Active Indicator Node */
.sb-nav-item.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 18%;
  height: 64%;
  width: 4px;
  background: #fb923c;
  border-radius: 0 4px 4px 0;
}

/* ── Lower Anchor Deck ── */
.sb-footer {
  display: none;
}

/* ── Backdrop UI Glass layer ── */
.sb-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.15);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 150;
  animation: sbFadeIn 0.25s ease-out;
}

@keyframes sbFadeIn { from { opacity: 0; } to { opacity: 1; } }

/* ── Floating Action Trigger ── */
.sb-fab {
  position: fixed;
  bottom: 24px;
  left: 24px;
  width: 52px;
  height: 52px;
  border: 1px solid var(--sb-border);
  border-radius: 16px;
  background: #ffffff;
  color: var(--sb-bg);
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 300;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.sb-fab:hover {
  transform: translateY(-2px) scale(1.04);
}

/* ── Fluid Adaptive Viewports ── */
@media (max-width: 1200px) {
  .sb {
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    box-shadow: 20px 0 40px rgba(15, 23, 42, 0.06);
  }
  .sb.open { transform: translateX(0); }
  .sb-close { display: flex; }
  .sb-fab { display: flex; }
  .sb-clinic-block { display: flex; }
}

@media (min-width: 769px) and (max-width: 1200px) {
  .sb {
    width: 65%;
    max-width: 65%;
  }
}

@media (max-width: 768px) {
  .sb { width: 100%; max-width: 100%; border-right: none; }
  .sb-header { padding: 0 20px; height: 80px; }
  .sb-body { padding: 16px 0; }
}

@media (prefers-reduced-motion: reduce) {
  .sb, .sb-nav-item, .sb-fab, .sb-overlay {
    transition: none !important;
    animation: none !important;
  }
}
`;

export default function Sidebar({
  mobileOpen: controlledMobileOpen,
  setMobileOpen: controlledSetMobileOpen,
}) {
  const location = useLocation();
  const [localMobileOpen, setLocalMobileOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  const { auth } = useAuth();
  const { subscriptionData } = useSubscription();
  const isExpired = subscriptionData?.mode === "EXPIRED";
  const clinicName = auth?.clinic?.name || "Clinic";
  const clinicCode = auth?.clinic?.code || "---";

  const isControlled = typeof controlledMobileOpen === "boolean";
  const mobileOpenState = isControlled ? controlledMobileOpen : localMobileOpen;
  const setMobileOpenState = controlledSetMobileOpen || setLocalMobileOpen;

  const isMobile = windowWidth <= 1200;

  const NAV_SECTIONS = [
    {
      title: "Main",
      items: [{ label: "Dashboard", path: "/admin-dashboard", icon: Home }]
    },
    {
      title: "Clinic Management",
      items: [
        { label: "Doctors", path: "/admin-dashboard/doctors", icon: Users },
        { label: "Appointments", path: "/admin-dashboard/appointments", icon: Calendar },
      ]
    },
    {
      title: "Records",
      items: [{ label: "Patients & Records", path: "/admin-dashboard/patients", icon: FileText }]
    },

    {
      title: "Billing",
      items: [
        { label: "Subscription and Invoices", path: "/admin-dashboard/subscription", icon: CreditCard },
      ]
    },
    {
      title: "Analytics",
      items: [{ label: "Revenue", path: "/admin-dashboard/revenue", icon: TrendingUp }]
    },
    {
      title: "System",
      items: [
        { label: "Settings", path: "/admin-dashboard/settings", icon: Settings }
      ]
    },
  ];

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setMobileOpenState(false);
  }, [location.pathname]);

  return (
    <>
      <style>{STYLES}</style>

      {/* Mobile Backdrop */}
      {isMobile && mobileOpenState && (
        <div
          className="sb-overlay"
          onClick={() => setMobileOpenState(false)}
        />
      )}

      {/* Sidebar Core Architecture */}
      <aside className={`sb${mobileOpenState ? " open" : ""}`}>

        {/* Branding Frame */}
        <div className="sb-header">
          <div className="sb-web_logo-container">
            <img src={web_logoImage} alt="web_logo" className="sb-web_logo" />
            <div className="sb-brand-divider" aria-hidden="true" />
            <div className="sb-clinic-block">
              <div className="sb-clinic-name-row">
                <Hospital size={16} className="sb-clinic-icon" />
                <span className="sb-clinic-name">{clinicName}</span>
              </div>
              <span className="sb-clinic-code">{clinicCode}</span>
            </div>
          </div>

          <button 
            className="sb-close" 
            onClick={() => setMobileOpenState(false)}
            aria-label="Close menu"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        {/* Content Node Streams */}
        <div className="sb-body">
          <div style={{ padding: "0 12px 16px 12px" }}>
            <ManageSessions isExpired={isExpired} />
          </div>
          
          {NAV_SECTIONS.map((section) => {
            return (
              <div key={section.title} className="sb-section">
                <div className="sb-section-title">{section.title}</div>
                <div className="sb-nav-list">
                  {section.items.map((item) => {
                    const isActive = item.path === "/admin-dashboard" 
                      ? location.pathname === item.path
                      : location.pathname.startsWith(item.path);
                    const Icon = item.icon;
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`sb-nav-item ${isActive ? "active" : ""}${isExpired && item.path === "/admin-dashboard/settings" ? " disabled" : ""}`}
                        onClick={(event) => {
                          if (isExpired && item.path === "/admin-dashboard/settings") {
                            event.preventDefault();
                            return;
                          }
                          if (isMobile) setMobileOpenState(false);
                        }}
                        aria-disabled={isExpired && item.path === "/admin-dashboard/settings"}
                        title={isExpired && item.path === "/admin-dashboard/settings" ? "Settings is unavailable while your subscription is expired" : item.label}
                      >
                        <Icon size={18} className="sb-nav-icon" />
                        <span className="sb-nav-label">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {/* larger spacer to ensure empty space below the last menu item (Revenue) */}
          <div style={{height: 36, width: '100%'}} />
        </div>
      </aside>

      {/* Mobile Menu Action FAB */}
      {!isControlled && isMobile && !mobileOpenState && (
        <button 
          className="sb-fab" 
          onClick={() => setMobileOpenState(true)}
          aria-label="Open menu"
        >
          <Menu size={22} strokeWidth={2} />
        </button>
      )}
    </>
  );
}
